import React, { ReactElement } from 'react'
import { RuleGroupType } from 'react-querybuilder'
import { Edge, Node } from 'reactflow'

import { Destination, NodeOutputSizeResult, SliceHealthStatus, SliceStatus, Source } from '@ui/__generated__/graphql'

export type DatableDataType = 'logs' | 'spans' | 'metrics'

export interface Pipeline {
  id: string
  name: DatableDataType
  enabled: boolean
  firehose: {
    size: NodeOutputSizeResult
  }
  slices: DatableNode[]
  numberOfSlices: number
  doesPipelineHaveErrors: boolean
  assignedSources: Source[]
}

export interface PipelineParams {
  currentNodeId: string
  codeChain: CodeChain[]
  pipelineId?: string
}

export interface PipelineData {
  pipelineList: Pipeline[]
  getLoaf: Pipeline
}

export interface QueryParams {
  where?: string
  startTime: Date
  endTime: Date
  from?: string
  limit?: number
  offset?: number
  nodeId?: string
}

export interface CodeChain {
  nodeId: string
  code: string
  parentNodeId?: string
}

export interface KeyValue {
  [key: string]: string | any | KeyValue
}

export interface DatableNode {
  id?: number
  code: string
  parentSliceId?: number
  loafId?: number
  type: NodeTypeEnum
  description?: string
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // This is dynamic customer data
  data?: any
  /* eslint-enable @typescript-eslint/no-explicit-any */
  slug?: string
  destination: Destination
  size: NodeOutputSizeResult
  status: SliceStatus
}

export interface DatableNodeData {
  [key: string]: string | number | boolean | null | DatableNodeData[] | KeyValue | undefined
  slug: string
  description: string
  size: NodeOutputSizeResult
  status: SliceStatus

  ogtype?: string
  highlight?: string
  undeletable?: boolean
  isMinimap?: boolean
  new?: boolean

  sliceId?: string | undefined
}

export interface TimeStates {
  timewindow: TimeWindow
}

export interface TimeWindow {
  english: string
  relative: [number, string]
}

export interface StartAndEnd {
  startTime: Date
  endTime: Date
}

export enum NodeTypeEnum {
  FIREHOSE = 'FIREHOSE',
  CODE = 'CODE',
  EXPORT = 'EXPORT',
  PLACEHOLDER = 'PLACEHOLDER',
  TRANSFORM = 'TRANSFORM',
}

export enum EdgeTypeEnum {
  LOG = 'LOG',
  SPAN = 'SPAN',
  METRIC = 'METRIC',
  PLACEHOLDER = 'PLACEHOLDER',
}

export enum TargetHandleEnum {
  TARGET_PLACEHOLDER = 'target_placeholder',
  TARGET_LOGS = 'target_log',
  TARGET_SPANS = 'target_span',
  TARGET_METRICS = 'target_metric',
}

export enum SourceHandleEnum {
  SOURCE_PLACEHOLDER = 'source_placeholder',
  SOURCE_LOGS = 'source_log',
  SOURCE_SPANS = 'source_span',
  SOURCE_METRICS = 'source_metric',
}

export enum PanelEnum {
  HIDDEN = 'HIDDEN',
  ADD_STEP = 'ADD_STEP',
  WIZARD = 'WIZARD',
  SOURCES = 'SOURCES',
  CODE = 'CODE',
  EXPORT = 'EXPORT',
  DESTINATIONS = 'DESTINATIONS',
}

export interface FormTableAttribute {
  [key: string]: string
}

export interface FormDataType {
  [key: string]: FormTableAttribute
}

export interface SaveTableAttribute {
  [key: string]: any
}

export interface SaveType {
  [key: string]: SaveTableAttribute
}

export const typeDefaults: KeyValue = {
  boolean: 'boolean',
  integer: 'integer',
  string: 'string',
  text: 'text',
  json: 'jsonb',
  object: 'jsonb',
  timestamp: 'timestamp',
}

export interface TableType {
  name: string
  schema: KeyValue
}

export interface Templates {
  getTemplates: Template[]
}

export interface Template {
  id: number
  name: string
  slug: string
  description: string
  config: object
  icon: string
  type: NodeTypeEnum | PanelEnum
  timestamp: Date
  enabled: boolean
  component: string
}

export interface ImportedIcons {
  [key: string]: React.FC<{ size: number; fill: string; className: string }>
}

export interface NodeStatusData {
  getNodeStatus: {
    logsStatus: SliceHealthStatus
    spansStatus: SliceHealthStatus
    metricsStatus: SliceHealthStatus
    logsEnabled: boolean
    spansEnabled: boolean
    metricsEnabled: boolean
    errors: NodeStatusError[]
  }
}
export interface NodeStatusError {
  message: string
  timestamp: string
}

export interface TelemetryData {
  telemetry: {
    uniqueKeys: AttributeKey[]
    events: TelemetryEvent<DatableLog | DatableTrace>[]
    topValues?: AttributeValue[]
    totals: Total
  }
}

export interface TopValuesData {
  telemetry: {
    topValues: {
      count: number
      key: string
      size: string
      value: string
    }[]
  }
}

export interface TelemetryEvent<EventType> {
  __typename: string
  record: EventType // DatableLog | DatableTrace
  metadata: Metadata
}

export interface DatableAttributes {
  [key: string]: DatableMessyData
}

export interface DatableResource {
  [key: string]: DatableMessyData
}

export interface DatableLogScope {
  [key: string]: DatableMessyData
}

export interface DatableTraceScope {
  name?: string
  version?: string
  schema_url?: string
  dropped_attributes_count?: number
  [key: string]: DatableMessyData
}

export interface DatableTraceEvent {
  timestamp: string //event.timeUnixNano,
  name: string //event.name,
  attributes: DatableAttributes
  dropped_attributes_count: number
}

export interface DatableTraceLink {
  attributes: DatableAttributes
  dropped_attributes_count?: number
  trace_id: string //Buffer.from(link.traceId).toString('hex'),
  span_id: string //Buffer.from(link.spanId).toString('hex'),
}

export type DatableKeyValue = { [key: string | number]: DatableMessyData }

export type DatableMessyData = string | number | boolean | null | DatableMessyData[] | DatableKeyValue | undefined

export interface DatableLog {
  // Required
  timestamp: string
  timestamp_nano: string
  attributes: DatableAttributes
  body: DatableMessyData

  // Optional
  observed_timestamp_nano?: string
  severity_number?: number
  severity_text?: string
  trace_flags?: number
  trace_id?: string
  span_id?: string
  resource?: DatableResource // TODO: Should this be resource or resources? datadog.ts convertJsonLogPayload line 40 may need a change
  scope?: DatableLogScope
}

export interface DatableTrace {
  // These are example resource types, leave them here because its easy to get lost in all the fields
  resource: {
    'service.name'?: string // 'featureflagservice'
    'service.instance.id'?: string // 'featureflagservice@07728cd1a65b'
    'process.runtime.version'?: string // '11.0'
    'process.runtime.name'?: string // 'BEAM'
    'process.runtime.description'?: string // 'Erlang/OTP 23 erts-11.0'
    'process.executable.name'?: string // 'featureflagservice'
    'library.version'?: string // '1.1.2'
    'library.name'?: string // 'opentelemetry'
    'library.language'?: string // 'erlang'
  } & { [key: string]: DatableMessyData }

  scope?: DatableTraceScope

  attributes: DatableAttributes
  events?: DatableTraceEvent[]
  links?: DatableTraceLink[]

  timestamp: string
  observed_timestamp_nano?: string

  trace_id: string // Buffer.from(span.traceId).toString('hex'),
  span_id: string // Buffer.from(span.spanId).toString('hex'),
  parent_id?: string

  name: string
  kind?: string
  start_time: string //span.startTimeUnixNano,
  end_time: string // span.endTimeUnixNano,
  dropped_links_count?: number //span.droppedLinksCount,
  dropped_attributes_count?: number //span.droppedAttributesCount,
  dropped_events_count?: number //span.droppedEventsCount,
  status_code?: number //span.status?.code || -1,
}

export interface DatableTraceDuration {
  record: DatableTrace
  duration: number
}

export interface Metadata {
  [key: string]: string
}
export interface AttributeKey {
  key: string
  count: number
  size: number
}

export interface AttributeValue {
  key: string
  value: string
  count: number
  size: number
  ratio: number
  queryProps: QueryProps
}

export interface Total {
  [count: string]: number
  size: number
}

export interface QueryProps {
  operator: string
  value: string
}

export interface Instance {
  id: number
  name: string
  description: string
  connect_key: string
  last_heartbeat: Date
  minutesSinceLastHeartbeat: number
  version: string
  organization?: Organization
  stacks: Stack[]
}

export interface Stack {
  uuid: string
  state: 'idle' | 'active' | 'decommissioned'
  gitref: string
  version: string
  last_heartbeat: string
  created_at: string
  updated_at: string
  decommissioned_at: string | null
  active_at: string | null
}

export interface DynamicFormValues {
  [key: string]: string | number | boolean | null | DynamicFormValues[] // other potential types
}

export interface VerifySessionResponse {
  member: Member
  organization: Organization
  instance: Instance
}

export interface Member {
  id: number
  email: string
  first_name: string
  last_name: string
  created_at: string
  updated_at: string
  current_sign_in_at: Date
  is_datable_admin: boolean
  role: Role | null
}

export enum Role {
  ADMIN = 'admin',
  STANDARD = 'standard',
}

export interface Organization {
  id: number
  name: string
  slug: string
  description?: string
  created_at?: string
  updated_at?: string
  email_allowed_domains: string[]
}

export interface SigninCredentialsPayload {
  user: {
    email: string
    password: string
  }
}

export interface SignupCredentialsPayload {
  user: {
    email: string
    password: string
    password_confirmation: string
    first_name?: string | null
    last_name?: string | null
  }
}

export interface Error {
  message: string
}

export interface AuthCallbackType {
  (err: Error | null, response?: undefined | VerifySessionResponse): void
}

export enum NotificationTypeEnum {
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
}

export enum ManagementPageStatus {
  INSTANCES = 'INSTANCES',
  MEMBERS = 'MEMBERS',
  INGRESS = 'INGRESS',
}

export enum SignupPageStatus {
  LOGIN = 'LOGIN',
  SIGNUP = 'SIGNUP',
  SUCCESS = 'SUCCESS',
  FORGOT = 'FORGOT',
  RESET = 'RESET',
  INVITED = 'INVITED',
  FAIL = 'FAIL',
  CONFIRM_EMAIL_NOTIFICATION = 'CONFIRM_EMAIL_NOTIFICATION',
  CONFIRM_EMAIL = 'CONFIRM_EMAIL',
  DEMO = 'DEMO',
}

export enum PageStatus {
  INITIAL = 'INITIAL',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export interface DatableEdge {
  id: number
  source_node_id: string
  target_node_id: string
  data_type: NodeTypeEnum
}

export interface SimulateEventParams {
  selectedNodeId: string
  selectedNodeCode: string
  selectedEvent: DatableLog | DatableTrace | undefined
  selectedEventType: 'log' | 'span'
  events: TelemetryEvent<DatableLog | DatableTrace>[]
  nodes: Node[]
  edges: Edge[]
  queryParams?: QueryParams
  skip?: boolean
}

export interface NodeError {
  id: string
  name: string
  sliceId: string
  errorMessage: string
  lastDay: number
  firstOccurrence: Date
  lastOccurrence: Date
  description: string
  timeseries: TimeseriesTick[]
  records: TelemetryEvent<DatableLog | DatableTrace>[]
  [key: string]: string | number | Date | TelemetryEvent<DatableLog | DatableTrace>[] | TimeseriesTick[]
}

interface TimeseriesTick {
  timestamp: Date
  volume: number
}

export interface LineChart {
  timeseries: TimeseriesTick[]
}

export interface ChartDataType {
  labels: string[]
  datasets: ChartDataset[]
}

export interface ChartDataset {
  label: string
  data: number[]
  backgroundColor: string
  borderColor: string
  fill: boolean
}

export interface SimulateChain {
  consoleLog: string
  errorLog: string
  input: TelemetryEvent<DatableLog | DatableTrace>[]
  output: TelemetryEvent<DatableLog | DatableTrace>[]
}

export interface DataViewerProps {
  data: SimulateChain[]
  isLoading: boolean
  lineChart?: LineChart
}

export interface DynamicFormProps {
  saveClick: (values: DynamicFormValues) => void
  cancelClick?: () => void
  config: KeyValue
  additionalInputs?: ReactElement
  submitButton?: ReactElement
  disableInputs?: boolean
}

export interface NodeButtonTrigger {
  action: NodeButtonAction
  position: NodeButtonPosition
}

export enum NodeButtonPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum NodeButtonAction {
  SPLIT = 'split',
  IN_BETWEEN = 'inBetween',
}

export interface ServiceGroup {
  serviceName: string
  name: string
  totalSize: number
  count: number
}

export interface FullTrace {
  traces: DatableTraceDuration[]
  totalDuration: number
}

export interface AttributeGroup {
  name: string
  values: {
    value: string
    count: number
  }[]
}

export enum TraceViewStates {
  GROUP = 'GROUP',
  ROOT_TRACES = 'ROOT_TRACES',
}

export enum TracesGroupBy {
  SERVICE = 'SERVICE',
  ATTRIBUTE = 'ATTRIBUTE',
}

export enum CodeTransformType {
  CODE = 'CODE',
  SAMPLE = 'SAMPLE',
  DROP = 'DROP',
}

export type NodeStepPredicate = {
  jsonata?: string
  ruleGroup?: RuleGroupType
  fields: string[]
}
